import React, { useMemo, useState } from 'react';
import axios from 'axios';
import domain from "../../../util/domain";
import {
    Box,
    Typography,
    Button,
    Sheet,
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/joy';
import SearchCollege from '../../inputs/SearchCollege';

export default function MapStudents() {
    const storedCollege = useMemo(() => {
        try {
            const raw = localStorage.getItem('mapSelectedCollege');
            return raw ? JSON.parse(raw) : null;
        } catch (error) {
            console.error('Error parsing stored mapSelectedCollege', error);
            return null;
        }
    }, []);

    const [colleges, setColleges] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedCollege, setSelectedCollege] = useState(storedCollege);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [activeStudent, setActiveStudent] = useState(null);
    const [selectAll, setSelectAll] = useState(false);
    const [openModal, setOpenModal] = useState(false);

    const fetchColleges = async (searchTerm = '') => {
        try {
            if (searchTerm === '' && searchTerm.length >= 4) {
                return;
            } else {
                setLoading(true);
                const response = await axios.get(`${domain}/students/unmapped-colleges?search=${searchTerm}`);
                //console.log(response.data);
                setColleges(response.data);
                setActiveStudent(null);
            }
        } catch (error) {
            console.error('Error fetching colleges:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchColleges(search);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedStudents(colleges.map(college => college.studentid));
            setSelectAll(true);
        } else {
            setSelectedStudents([]);
            setSelectAll(false);
        }
    };

    const handleSelectStudent = (studentId) => {
        setSelectedStudents(prev => {
            if (prev.includes(studentId)) {
                const newSelection = prev.filter(id => id !== studentId);
                setSelectAll(false);
                return newSelection;
            } else {
                const newSelection = [...prev, studentId];
                setSelectAll(newSelection.length === colleges.length);
                return newSelection;
            }
        });
    };

    const handleMapSelected = async () => {
        if (!selectedCollege || selectedStudents.length === 0) {
            return;
        }
        setOpenModal(true);
    };

    const handleConfirmMap = async () => {
        try {
            const response = await axios.post(`${domain}/students/map`, {
                studentIds: selectedStudents,
                collegeCode: selectedCollege.college_code
            });
            //console.log(response);
            setOpenModal(false);
            fetchColleges(search);
            setSelectedStudents([]);
            setSelectAll(false);

        } catch (error) {
            console.error("Error mapping students:", error);
        }
    };

    const handleCollegeChange = (college) => {
        setSelectedCollege(college);
        if (college) {
            localStorage.setItem('mapSelectedCollege', JSON.stringify(college));
        } else {
            localStorage.removeItem('mapSelectedCollege');
        }
    };

    return (
        <>
            <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <ModalDialog>
                    <DialogTitle>Confirm Mapping</DialogTitle>
                    <DialogContent>
                        Are you sure you want to map {selectedStudents.length} student(s) to {selectedCollege?.college_name}?
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={() => setOpenModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            onClick={handleConfirmMap}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>

            <div className="container-fluid">
                {/* Top Search Bar */}
                <div className="row mb-4 mt-3">
                    <div className="col-12">
                        <form onSubmit={handleSearch}>
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search students by college name..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                <button type="submit" className="btn btn-primary">
                                    {loading ? 'Searching...' : 'Search'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Split View */}
                <div className="row" style={{ height: 'calc(100vh - 200px)' }}>
                    {/* Left Side - Students List */}
                    <div className="col-md-6 pe-md-2">
                        <div className="card h-100">
                            <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Students List</h5>
                                <div>
                                    <span className="me-2">
                                        {selectedStudents.length} selected
                                    </span>

                                </div>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
                                    <table className="table table-hover table-striped mb-0">
                                        <thead className="sticky-top bg-white">
                                            <tr>
                                                <th>
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={selectAll}
                                                            onChange={handleSelectAll}
                                                        />
                                                    </div>
                                                </th>
                                                <th>Student ID</th>
                                                <th>YOP</th>
                                                <th>Mobile</th>
                                                <th>College</th>
                                                <th>university</th>
                                                <th>collegeaddress</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {colleges.map((college) => (
                                                <tr
                                                    key={college.studentid}
                                                    onClick={() => setActiveStudent(college)}
                                                    className={activeStudent?.studentid === college.studentid ? 'table-primary' : ''}
                                                >
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <div className="form-check">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={selectedStudents.includes(college.studentid)}
                                                                onChange={() => handleSelectStudent(college.studentid)}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>{college.studentid}</td>
                                                    <td>{college.yop}</td>
                                                    <td>{college.mobileno}</td>
                                                    <td>{college.college}</td>
                                                    <td>{college.university}</td>
                                                    <td>{college.collegeaddress}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {colleges.length === 0 && !loading && (
                                        <div className="text-center p-3">
                                            No students found
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - College Selection */}
                    <div className="col-md-6 ps-md-2">
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', pt: { md: 0, xs: 4 } }}>
                            <Typography level="h4" component="h1" sx={{ mb: 2 }}>
                                College Search
                            </Typography>
                            <Sheet
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderRadius: 'sm',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                <SearchCollege
                                    college={selectedCollege}
                                    onCollegeChange={handleCollegeChange}
                                    label="Select College"
                                    textFieldProps={{
                                        fullWidth: true,
                                    }}
                                />
                                {selectedCollege && (
                                    <Box
                                        sx={{
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            p: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                        }}
                                    >
                                        <Typography level="title-sm" sx={{ color: '#eb7a24' }}>
                                            {selectedCollege.college_name}
                                        </Typography>
                                        {selectedCollege.college_code && (
                                            <Typography level="body-sm">
                                                Code: {selectedCollege.college_code}
                                            </Typography>
                                        )}
                                        {selectedCollege.college_address && (
                                            <Typography level="body-sm">
                                                Address: {selectedCollege.college_address}
                                            </Typography>
                                        )}
                                        {selectedCollege.univerisity_name && (
                                            <Typography level="body-sm">
                                                University: {selectedCollege.univerisity_name}
                                            </Typography>
                                        )}
                                        {selectedCollege.college_website && (
                                            <Typography level="body-sm">
                                                Website: {selectedCollege.college_website}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                <Button
                                    variant="solid"
                                    onClick={handleMapSelected}
                                    disabled={!selectedCollege || selectedStudents.length === 0}
                                >
                                    Map Selected {selectedStudents.length > 0 ? `(${selectedStudents.length})` : ''}
                                </Button>
                            </Sheet>
                        </Box>
                    </div>
                </div>
            </div>
        </>
    );
}
